import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Restore as RestoreIcon,
} from "@mui/icons-material";
import { useSettings } from "../../hooks/useRedux";

const Settings: React.FC = () => {
  const {
    settings: apiSettings,
    loading,
    error,
    updateSettings,
  } = useSettings();
  const [settings, setSettings] = useState({
    app: {
      maintenanceMode: false,
      allowNewRegistrations: true,
      requireEmailVerification: true,
    },
    branding: {
      appName: "Wellness App",
      primaryColor: "#1976d2",
      secondaryColor: "#dc004e",
      logoUrl: "",
      supportEmail: "support@wellnessapp.com",
    },
    notifications: {
      pushNotifications: true,
      emailNotifications: true,
      achievementNotifications: true,
    },
  });

  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [saveError, setSaveError] = useState<string>("");

  // Update local settings when API settings load or from localStorage
  React.useEffect(() => {
    if (apiSettings) {
      setSettings((prev) => ({
        app: { ...prev.app, ...(apiSettings.app || {}) },
        branding: { ...prev.branding, ...(apiSettings.branding || {}) },
        notifications: { ...prev.notifications, ...(apiSettings.notifications || {}) },
      }));
    } else {
      // Try to load from localStorage as fallback
      try {
        const savedSettings = localStorage.getItem('appSettings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          setSettings((prev) => ({
            app: { ...prev.app, ...(parsed.app || {}) },
            branding: { ...prev.branding, ...(parsed.branding || {}) },
            notifications: { ...prev.notifications, ...(parsed.notifications || {}) },
          }));
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [apiSettings]);

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    );
  }

  // Don't show error if it's just that the endpoint doesn't exist
  // The page will work with default settings
  const showError = error && typeof error === 'string' && !error.includes('404') && !error.includes('Not Found');

  const handleNotificationChange = (key: string, value: boolean) => {
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [key]: value,
      },
    });
  };

  const handleAppChange = (key: string, value: boolean) => {
    setSettings({
      ...settings,
      app: {
        ...settings.app,
        [key]: value,
      },
    });
  };

  const handleBrandingChange = (key: string, value: string) => {
    setSettings({
      ...settings,
      branding: {
        ...settings.branding,
        [key]: value,
      },
    });
  };

  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      // Basic validation
      if (!settings.branding.supportEmail.includes("@")) {
        throw new Error("Please enter a valid support email");
      }

      await updateSettings(settings);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      setSaveStatus("error");
      setSaveError(
        error instanceof Error ? error.message : "Failed to save settings"
      );
      setTimeout(() => {
        setSaveStatus("idle");
        setSaveError("");
      }, 5000);
      console.error("Error saving settings:", error);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      app: {
        maintenanceMode: false,
        allowNewRegistrations: true,
        requireEmailVerification: true,
      },
      branding: {
        appName: "Wellness App",
        primaryColor: "#1976d2",
        secondaryColor: "#dc004e",
        logoUrl: "",
        supportEmail: "support@wellnessapp.com",
      },
      notifications: {
        pushNotifications: true,
        emailNotifications: true,
        achievementNotifications: true,
      },
    });
  };

  return (
    <Box>
      <Typography variant='h4' gutterBottom>
        Wellness App Settings
      </Typography>

      {showError && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Note: Settings backend endpoint is not available. Changes will be saved locally.
        </Alert>
      )}

      {saveStatus === "saved" && (
        <Alert severity='success' sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      {saveStatus === "error" && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {saveError || "Failed to save settings. Please try again."}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* App Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <SecurityIcon sx={{ mr: 1 }} />
                <Typography variant='h6'>App Control</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.app.maintenanceMode}
                      onChange={(e) =>
                        handleAppChange("maintenanceMode", e.target.checked)
                      }
                    />
                  }
                  label='Maintenance Mode'
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.app.allowNewRegistrations}
                      onChange={(e) =>
                        handleAppChange(
                          "allowNewRegistrations",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label='Allow New Registrations'
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.app.requireEmailVerification}
                      onChange={(e) =>
                        handleAppChange(
                          "requireEmailVerification",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label='Require Email Verification'
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notifications Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <NotificationsIcon sx={{ mr: 1 }} />
                <Typography variant='h6'>Notifications</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.pushNotifications}
                      onChange={(e) =>
                        handleNotificationChange(
                          "pushNotifications",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label='Push Notifications'
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.emailNotifications}
                      onChange={(e) =>
                        handleNotificationChange(
                          "emailNotifications",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label='Email Notifications'
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.achievementNotifications}
                      onChange={(e) =>
                        handleNotificationChange(
                          "achievementNotifications",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label='Achievement Notifications'
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Branding Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display='flex' alignItems='center' mb={2}>
                <PaletteIcon sx={{ mr: 1 }} />
                <Typography variant='h6'>App Branding</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label='App Name'
                    value={settings.branding.appName}
                    onChange={(e) =>
                      handleBrandingChange("appName", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label='Support Email'
                    type='email'
                    value={settings.branding.supportEmail}
                    onChange={(e) =>
                      handleBrandingChange("supportEmail", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label='Logo URL'
                    value={settings.branding.logoUrl}
                    onChange={(e) =>
                      handleBrandingChange("logoUrl", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label='Primary Color'
                    type='color'
                    value={settings.branding.primaryColor}
                    onChange={(e) =>
                      handleBrandingChange("primaryColor", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label='Secondary Color'
                    type='color'
                    value={settings.branding.secondaryColor}
                    onChange={(e) =>
                      handleBrandingChange("secondaryColor", e.target.value)
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Button
              variant='outlined'
              startIcon={<RestoreIcon />}
              onClick={resetToDefaults}
              disabled={saveStatus === "saving"}
            >
              Reset to Defaults
            </Button>
            <Button
              variant='contained'
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveStatus === "saving"}
            >
              {saveStatus === "saving" ? "Saving..." : "Save Settings"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
